import { apiClient } from './api-client'
import { getErrorMessage, getErrorStatus } from '../utils/error.utils'

export interface SyncResult {
  success: boolean
  message: string
  count?: number
  timestamp: string
}

export interface SyncStatus {
  success: boolean
  data: {
    totalPublications: number
    recentPublications: number
    activeLawyers: number
    lastUpdate: string
  }
  timestamp: string
}

export interface ConnectivityTest {
  success: boolean
  message: string
  responseTime?: number
}

class DJENSyncService {
  private readonly baseUrl = '/api/djen'

  /**
   * Trigger manual sync for all lawyers
   */
  async triggerManualSync(): Promise<SyncResult> {
    try {
      // DJEN sync can take 15-30 seconds depending on number of lawyers and publications
      const response = await apiClient.post(`${this.baseUrl}/sync/manual`, {}, {
        timeout: 60000 // 60 seconds timeout for manual sync
      })
      return response.data
    } catch (error: unknown) {
      console.error('DJEN Sync Error:', error)
      const status = getErrorStatus(error)
      const serverMessage = getErrorMessage(error, '')

      if (status === 401) {
        throw new Error('Sessão expirada. Faça login novamente para executar a sincronização.')
      } else if (status === 403) {
        throw new Error('Permissão negada. Você não tem autorização para executar sincronizações manuais.')
      } else if (status === 500) {
        if (serverMessage.includes('audit_logs')) {
          throw new Error('Erro interno do servidor: problema com auditoria. Contate o administrador.')
        } else if (serverMessage.includes('database')) {
          throw new Error('Erro de banco de dados. Contate o administrador do sistema.')
        } else {
          throw new Error(`Erro interno do servidor: ${serverMessage || 'erro desconhecido'}`)
        }
      } else {
        throw new Error(`Erro ao executar sincronização manual: ${serverMessage || 'Erro desconhecido'}`)
      }
    }
  }

  /**
   * Sync publications for specific OAB
   */
  async syncSpecificOAB(numeroOab: string, ufOab: string): Promise<SyncResult> {
    try {
      const response = await apiClient.post(`${this.baseUrl}/sync/oab`, {
        numeroOab,
        ufOab
      }, {
        timeout: 30000 // 30 seconds timeout for single OAB sync
      })
      return response.data
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error, 'Erro ao sincronizar OAB específica'))
    }
  }

  /**
   * Test API connectivity
   */
  async testConnectivity(numeroOab: string, ufOab: string): Promise<ConnectivityTest> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/sync/test`, {
        params: { numeroOab, ufOab }
      })
      return response.data
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error, 'Erro ao testar conectividade'))
    }
  }

  /**
   * Get sync status and statistics
   */
  async getSyncStatus(): Promise<SyncStatus> {
    try {
      const response = await apiClient.get(`${this.baseUrl}/sync/status`)
      return response.data
    } catch (error: unknown) {
      throw new Error(getErrorMessage(error, 'Erro ao obter status da sincronização'))
    }
  }
}

export const djenSyncService = new DJENSyncService()